import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiDownload, FiFileText, FiX } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { extensionOf, formatBytes } from "../utils/chatFiles";

/** A photo opened full size over the page, with a way to save it. */
function Lightbox({ src, downloadUrl, name, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex flex-col bg-overlay-scrim" onClick={onClose} role="dialog" aria-label={name}>
      <div className="flex items-center justify-between gap-3 p-3 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="min-w-0 truncate text-subheadline font-medium">{name}</span>
        <div className="flex shrink-0 gap-1">
          <a href={downloadUrl} download={name} className="grid h-10 w-10 place-items-center rounded-full hover:bg-white/10" title={t("chatFiles.download")} aria-label={t("chatFiles.download")}>
            <FiDownload className="h-5 w-5" />
          </a>
          <button type="button" onClick={onClose} className="grid h-10 w-10 place-items-center rounded-full hover:bg-white/10" title={t("common.close")} aria-label={t("common.close")}>
            <FiX className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-3 pt-0">
        <img src={src} alt={name} className="max-h-full max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
      </div>
    </div>,
    document.body
  );
}

/**
 * A photo, video or file inside a chat bubble. Photos open full size; videos play in place;
 * files download. `url` shows it, `downloadUrl` saves it. `own` is the sender's (mint) bubble.
 */
const ChatAttachment = ({ attachment, url, downloadUrl, own = false, isDark, compact = false }) => {
  const { t } = useTranslation();
  const [zoomed, setZoomed] = useState(false);
  const [broken, setBroken] = useState(false);
  if (!attachment?.kind) return null;

  const maxH = compact ? "max-h-52" : "max-h-72";
  const name = attachment.name || t("chatFiles.file");

  if (attachment.kind === "image" && !broken) {
    return (
      <>
        <button type="button" onClick={() => setZoomed(true)} className="block max-w-full overflow-hidden rounded-[20px]" title={name}>
          <img
            src={url}
            alt={name}
            loading="lazy"
            onError={() => setBroken(true)}
            className={`${maxH} ${compact ? "min-h-[64px] min-w-[96px]" : "min-h-[80px] min-w-[120px]"} w-auto max-w-full object-cover`}
          />
        </button>
        {zoomed && <Lightbox src={url} downloadUrl={downloadUrl} name={name} onClose={() => setZoomed(false)} />}
      </>
    );
  }

  if (attachment.kind === "video" && !broken) {
    return (
      <video
        src={url}
        controls
        playsInline
        preload="metadata"
        onError={() => setBroken(true)}
        className={`${maxH} w-full max-w-full rounded-[20px] bg-black`}
      />
    );
  }

  const ext = extensionOf(name);
  return (
    <a
      href={downloadUrl}
      download={name}
      className={`flex min-w-0 items-center gap-3 rounded-radius-sm px-2.5 py-2 transition ${
        own ? "bg-ink/10 hover:bg-ink/15" : "bg-fill-tertiary hover:bg-fill-quaternary"
      } ${compact ? "max-w-[220px]" : "max-w-[300px]"}`}
      title={t("chatFiles.download")}
    >
      <span
        className={`relative grid shrink-0 place-items-center rounded-radius-xs ${compact ? "h-9 w-9" : "h-10 w-10"} ${
          own ? "bg-ink/15 text-accent-on" : isDark ? "bg-white/10 text-slate-200" : "bg-white text-slate-600"
        }`}
      >
        <FiFileText className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block truncate font-semibold ${compact ? "text-footnote" : "text-subheadline"}`}>{name}</span>
        <span className={`block text-caption-1 ${own ? "text-accent-on opacity-60" : "text-label-secondary"}`}>
          {[ext, formatBytes(attachment.size)].filter(Boolean).join(" · ")}
        </span>
      </span>
      <FiDownload className={`h-4 w-4 shrink-0 ${own ? "text-accent-on opacity-70" : "text-label-secondary"}`} />
    </a>
  );
};

export default ChatAttachment;
